import { useEffect, useState } from "react";
import { X, Download, FileText } from "lucide-react";

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isImageFile(file) {
  return file.type.startsWith("image/");
}

function isPdfFile(file) {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

// Previews a not-yet-uploaded browser File object: image thumbnail, native-browser
// PDF page-1 preview (via <embed>, no pdf.js dependency needed), or a generic icon
// for anything else (e.g. .doc/.docx, which have no client-renderable preview).
export default function FilePreview({ file, label, onRemove }) {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function download() {
    if (!objectUrl) return;
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <div className="flex items-center gap-3 bg-white/5 rounded-xl p-2.5" data-testid="file-preview-card">
      <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-black/30 grid place-items-center border border-white/10">
        {objectUrl && isImageFile(file) && (
          <img src={objectUrl} alt={file.name} className="w-full h-full object-cover" />
        )}
        {objectUrl && isPdfFile(file) && (
          <embed src={`${objectUrl}#page=1`} type="application/pdf" className="w-full h-full pointer-events-none" />
        )}
        {objectUrl && !isImageFile(file) && !isPdfFile(file) && (
          <FileText size={20} className="text-white/40" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {label && <p className="text-[10px] uppercase tracking-wide text-[var(--gold)] mb-0.5">{label}</p>}
        <p className="text-sm text-white/85 truncate">{file.name}</p>
        <p className="text-xs text-white/45">{formatFileSize(file.size)}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={download}
          className="w-8 h-8 grid place-items-center rounded-full text-white/50 hover:text-[var(--gold)] hover:bg-white/5 transition"
          title="Download"
          data-testid="file-preview-download"
        >
          <Download size={14} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="w-8 h-8 grid place-items-center rounded-full text-white/50 hover:text-red-400 hover:bg-red-500/10 transition"
          title="Remove"
          data-testid="file-preview-remove"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
